import api from "../../services/api";
import { Eventbus } from "../../utils";
import { events } from "../../utils/constants";
import { mergeCollection } from "../../utils/helpers";
import logger from "../../utils/logger";
import { format } from 'date-fns-tz';

const ExportService = (function () {
    // Private data cache
    let data = [];
    let isListenerRegistered = false;
    let isLoading = false; // Add loading flag to prevent duplicate requests
    let pendingLoad = null;
    let pendingDeleteId = null; // Store the ID of export pending deletion
    let isDeleting = false; // Track deletion state

    function getData() {
        return [...data]; // Return a copy to prevent external mutation
    }

    function updateExport(updatedRecord) {
        try {
            if (!updatedRecord || !updatedRecord.exportBatchId) {
                logger.warn('Invalid export record received for update:', updatedRecord);
                return;
            }

            // Work on a copy to avoid mutation during concurrent reads
            const updated = [...data];
            mergeCollection([updatedRecord], updated, { 
                idField: 'exportBatchId', 
                event: events.EXPORT_LOADED 
            });
            data = updated;
            
            logger.debug('Export record processed:', updatedRecord.exportBatchId);
        } catch (error) {
            logger.error('Error updating export record:', error, updatedRecord);
        }
    }

    function updateDeletedExport(deletedId) {
        try {
            if (!deletedId) {
                logger.warn('Invalid export ID received for deletion:', deletedId);
                return;
            }

            // Work on a copy to avoid mutation during concurrent reads
            const updated = [...data];
            mergeCollection([{ exportBatchId: deletedId }], updated, { 
                idField: 'exportBatchId', 
                hardDelete: true, 
                event: events.EXPORT_LOADED 
            });
            data = updated;
            logger.debug('Deleted export record:', deletedId);
        } catch (error) {
            logger.error('Error deleting export record:', error, deletedId);
        }
    }

    function loadData(forceRefresh = false) {
        // Reuse the in-flight request so callers don't race against an empty cache.
        if (isLoading && pendingLoad) {
            return pendingLoad.then((result) => [...result]);
        }

        // If data exists and we're not forcing a refresh, return the cached data
        if (data.length > 0 && !forceRefresh) {
            isLoading = false; // Ensure loading state is reset when returning cached data
            Eventbus.emit(events.EXPORT_LOADED, [...data]);
            return Promise.resolve([...data]);
        }

        isLoading = true;

        // Otherwise fetch fresh data from the API
        pendingLoad = api
            .getExports()
            .then((response) => {
                if (response.code === 'ERR_CANCELED') {
                    return [...data]; // Return existing data on cancellation
                }
                data = response || []; // Store the response data in the data variable
                Eventbus.emit(events.EXPORT_LOADED, [...data]);

                // Only register the event listener once
                if (!isListenerRegistered) {
                    Eventbus.on(events.SIGNALR.EXPORT_DATA_CHANGES, updateExport);
                    Eventbus.on(events.SIGNALR.EXPORT_DELETED, updateDeletedExport);
                    isListenerRegistered = true;
                    logger.debug('SignalR export data changes listener registered');
                }

                return [...data];
            })
            .catch((error) => {
                logger.error('Error fetching export records:', error);
                return [];
            })
            .finally(() => {
                isLoading = false;
                pendingLoad = null;
            });

        return pendingLoad;
    }

    function init() {
        return loadData(true);
    }

    function createExport() {
        return api.createExport()
            .then((response) => {
                if (response && response.code === 'ERR_CANCELED') {
                    return null; // Return null on cancellation
                }
                logger.debug('Export created successfully:', response);
                return response; // Return the created export data
            })
            .catch((error) => {
                logger.error('Error creating export:', error);
                throw error; // Propagate the error
            });
    }

    function getDocument(id) {
        return api.getExportDocument(id)
            .then((response) => {
                if (response.code === 'ERR_CANCELED') {
                    return null; // Return null on cancellation
                }
                logger.debug('Document retrieved successfully:', response);
                return response; // Return the document data
            })
            .catch((error) => {
                logger.error('Error retrieving Document:', error);
                throw error; // Propagate the error
            });
    }

    async function downloadExportDocument(exportRecord) {
        try {
            const fileData = await getDocument(exportRecord?.exportBatchId);
            if (!fileData) return;
            // Try to get the export record for filename/date
            let filename = `export.csv`;
            if (exportRecord && exportRecord.postingDate) {
                const formattedDate = format(exportRecord.postingDate, 'yyyy-MM-dd', { timeZone: 'Pacific/Auckland' });
                filename = `${filename}_${formattedDate}.csv`;
            }
            const blob = new Blob([fileData], { type: "text/csv" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            a.click();
            window.URL.revokeObjectURL(url);
            a.remove(); // Clean up the anchor element
        } catch (error) {
            // eslint-disable-next-line no-console
            logger.error("Error downloading export file:", error);
            throw error;
        }
    }

    function getLoadingState() {
        return isLoading;
    }

    function setPendingDelete(exportId) {
        pendingDeleteId = exportId;
    }

    function getPendingDelete() {
        return pendingDeleteId;
    }

    function clearPendingDelete() {
        pendingDeleteId = null;
    }

    function getDeletingState() {
        return isDeleting;
    }

    function deleteExport(id) {
        isDeleting = true;
        return api.deleteExport(id)
            .then((response) => {
                if (response.code === 'ERR_CANCELED') {
                    return null; // Return null on cancellation
                }
                logger.debug('Export deleted successfully:', response);

                return response; // Return the deletion response
            })
            .catch((error) => {
                logger.error('Error deleting export:', error);
                throw error; // Propagate the error
            })
            .finally(() => {
                isDeleting = false;
                clearPendingDelete();
            });
    }

    function clear() {
        if (isListenerRegistered) {
            Eventbus.off(updateExport);
            Eventbus.off(updateDeletedExport);
            isListenerRegistered = false;
            logger.debug('SignalR export data changes listener removed');
        }
        data = [];
        pendingLoad = null;
        isLoading = false; // Reset loading state
        pendingDeleteId = null; // Reset delete state
        isDeleting = false;
    }



    return {
        getData,
        loadData,
        init,
        clear,
        updateExport, // For SignalR updates
        getLoadingState, // Export loading state
        updateDeletedExport,
        createExport,
        downloadExportDocument,
        deleteExport,
        setPendingDelete,
        getPendingDelete,
        clearPendingDelete,
        getDeletingState
    };
})();

export default ExportService;
